import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Avatar, Chip } from '@mui/material';
import { selectThreadTyping } from '../../slice/threadSlice.jsx';

const ThreadTypingIndicator = ({ threadId }) => {
  const threadTyping = useSelector(selectThreadTyping(threadId));
  
  if (!threadTyping || Object.keys(threadTyping).length === 0) {
    return null;
  }

  const typingUsers = Object.values(threadTyping).filter(user => user.isTyping);
  
  if (typingUsers.length === 0) {
    return null;
  }

  const renderTypingText = () => {
    if (typingUsers.length === 1) {
      return `${typingUsers[0].senderName} is typing...`;
    } else if (typingUsers.length === 2) {
      return `${typingUsers[0].senderName} and ${typingUsers[1].senderName} are typing...`;
    } else {
      return `${typingUsers[0].senderName} and ${typingUsers.length - 1} others are typing...`;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        padding: '8px 16px',
        backgroundColor: 'rgba(25, 118, 210, 0.05)',
        borderRadius: '12px',
        margin: '8px 0',
        border: '1px solid rgba(25, 118, 210, 0.1)',
        animation: 'fadeInUp 0.3s ease-out',
        '@keyframes fadeInUp': {
          from: {
            opacity: 0,
            transform: 'translateY(10px)'
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)'
          }
        }
      }}
    >
      {/* User Avatars */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {typingUsers.slice(0, 3).map((user, index) => (
          <Avatar
            key={user.senderName}
            sx={{
              width: 24,
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 'bold',
              backgroundColor: '#1976d2',
              marginLeft: index > 0 ? '-8px' : '0',
              border: '2px solid white',
              zIndex: 3 - index
            }}
          >
            {user.senderName?.slice(0, 2).toUpperCase()}
          </Avatar>
        ))}
        
        {typingUsers.length > 3 && (
          <Chip
            label={`+${typingUsers.length - 3}`}
            size="small"
            sx={{
              height: '20px',
              fontSize: '0.7rem',
              marginLeft: '-4px',
              backgroundColor: '#1976d2',
              color: 'white',
              '& .MuiChip-label': {
                padding: '0 6px'
              }
            }}
          />
        )}
      </Box>

      {/* Typing Text with Animation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#1976d2', 
            fontWeight: 500,
            fontSize: '0.875rem'
          }}
        >
          {renderTypingText()}
        </Typography>
        
        {/* Animated Dots */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            marginLeft: '4px'
          }}
        >
          {[0, 1, 2].map((dot) => (
            <Box
              key={dot}
              sx={{
                width: '4px',
                height: '4px',
                backgroundColor: '#1976d2',
                borderRadius: '50%',
                animation: 'bounce 1.4s infinite ease-in-out',
                animationDelay: `${dot * 0.2}s`,
                '@keyframes bounce': {
                  '0%, 80%, 100%': {
                    transform: 'scale(0)',
                    opacity: 0.5
                  },
                  '40%': {
                    transform: 'scale(1)',
                    opacity: 1
                  }
                }
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ThreadTypingIndicator;
